import React, { useCallback, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useTranslation } from "react-i18next";

import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import qs from "query-string";

import { useMutation } from "@apollo/client";
import { MUTATION_RESET_PASSWORD } from "../../config/graphql/mutation";

import { useAuth } from "../../context/Auth";

import { useDomainCheck } from "../../lib/hooks/useDomainCheck";

import DefaultResetPassword from "./DefaultResetPassword";
import CenterParcsResetPassword from "./CenterParcsResetPassword";

type FieldValues = {
  password: string;
  confirmPassword: string;
};

const ResetPassword = React.memo(() => {
  const location = useLocation();
  const history = useHistory();

  const { t } = useTranslation("resetPassword");

  const { logout } = useAuth();

  const { isCenterParcsDomain } = useDomainCheck();

  const query = useMemo(
    () =>
      qs.parse(location.search, { parseNumbers: true }) as {
        code?: string;
      },
    [location.search],
  );

  useEffect(() => {
    if (!query.code) {
      history.push("/login");
    }
  }, [query.code, history]);

  const schema = useMemo(() => {
    return yup.object().shape({
      password: yup
        .string()
        .required(t("resetPassword:yup.password.required"))
        .min(8, t("resetPassword:yup.password.min", { count: 8 }))
        .matches(/[a-z]+/, t("resetPassword:yup.password.lowercase"))
        .matches(/[A-Z]+/, t("resetPassword:yup.password.uppercase"))
        .matches(/\d+/, t("resetPassword:yup.password.number"))
        .matches(/[@$!%*#?&]+/, t("resetPassword:yup.password.special")),
      confirmPassword: yup
        .string()
        .required(t("resetPassword:yup.confirmPassword.required"))
        .test(
          "match",
          t("resetPassword:yup.confirmPassword.match"),
          function validate(value) {
            return this.parent.password === value;
          },
        ),
    });
  }, [t]);

  const methods = useForm<FieldValues>({
    resolver: yupResolver(schema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  const [onResetPassword, { loading }] = useMutation(MUTATION_RESET_PASSWORD);

  const onSubmit = useCallback(
    (input: FieldValues) => {
      return onResetPassword({
        variables: { input: { code: query.code, password: input.password } },
      })
        .then(() => {
          toast.success<string>(t("resetPassword:toast.success"));

          logout();
        })
        .catch((error) => {
          const errorMessage =
            error?.networkError?.result?.errors?.[0]?.message ?? error?.message;

          toast.error<string>(errorMessage || t("resetPassword:toast.error"));
        });
    },
    [onResetPassword, query.code, t, logout],
  );

  const resetPasswordProps = {
    onSubmit: methods.handleSubmit(onSubmit),
    loading,
    t,
  };

  if (isCenterParcsDomain) {
    return (
      <FormProvider {...methods}>
        <CenterParcsResetPassword {...resetPasswordProps} />
      </FormProvider>
    );
  }

  return (
    <FormProvider {...methods}>
      <DefaultResetPassword {...resetPasswordProps} />
    </FormProvider>
  );
});

export default ResetPassword;
